'use client';

import { useState, useEffect } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { HealthVoxMark } from '@/components/layout/healthvox-mark';
import Link from 'next/link';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, Stethoscope, ShieldCheck } from 'lucide-react';
import { eventLogger } from '@/services/auth';
import { setUser, track } from '@inleads/event-logger';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REMEMBER_KEY = '@healthvox_remember_email';

export default function PatientLoginPage() {
  const router = useRouter();

  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [showPass, setShowPass]     = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading]   = useState(false);
  const [mounted, setMounted]       = useState(false);

  const [emailError, setEmailError]       = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [generalError, setGeneralError]   = useState('');

  useEffect(() => {
    setMounted(true);
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) { setEmail(saved); setRememberMe(true); }
    } catch { /* ignore */ }
  }, []);

  const validate = () => {
    let ok = true;
    setEmailError(''); setPasswordError(''); setGeneralError('');
    if (!email.trim()) { setEmailError('Email is required.'); ok = false; }
    else if (!EMAIL_RE.test(email.trim())) { setEmailError('Enter a valid email address.'); ok = false; }
    if (!password) { setPasswordError('Password is required.'); ok = false; }
    return ok;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, email.trim().toLowerCase());
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      const result = await signIn('patient-credentials', {
        email: email.trim().toLowerCase(),
        password,
        redirect: false,
      });
      if (result?.error) {
        setGeneralError(result.error === 'CredentialsSignin' ? 'Invalid email or password. Please try again.' : result.error);
      } else if (result?.ok) {
        eventLogger();
        await setUser(email.trim().toLowerCase());
        await track('LOGIN');
        router.push('/patient/dashboard');
        router.refresh();
      }
    } catch (err: any) {
      setGeneralError(err.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-gradient-to-br from-slate-50 via-indigo-50/60 to-blue-50">
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6">
        <div className={`w-full max-w-sm transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

          {/* ── Branding ── */}
          <div className={`text-center mb-4 transition-all duration-500 ease-out delay-100 ${mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}`}>
            <HealthVoxMark className="inline-block h-12 w-12 sm:h-16 sm:w-16 mb-2.5 text-indigo-500 drop-shadow-lg" />
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Welcome Back</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Sign in to continue your health journey</p>
          </div>

          {/* ── Card ── */}
          <div className={`bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden transition-all duration-500 ease-out delay-150 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="px-5 pt-5 pb-2">
              <h2 className="text-base font-semibold text-slate-800">Sign In</h2>
            </div>

            <form onSubmit={handleLogin} className="px-5 pb-4 pt-2 space-y-3" noValidate>

              {generalError && (
                <div className="flex items-start gap-2.5 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5">
                  <svg className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-600">{generalError}</p>
                </div>
              )}

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Email</label>
                <div className={`flex h-10 w-full rounded-xl border bg-slate-50 overflow-hidden transition-all ${emailError ? 'border-red-400 bg-red-50 ring-1 ring-red-300' : 'border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-200'}`}>
                  <span className="flex items-center pl-3 pr-2">
                    <Mail className={`h-4 w-4 ${emailError ? 'text-red-400' : 'text-slate-400'}`} />
                  </span>
                  <input type="email" autoComplete="email" placeholder="you@example.com"
                    value={email} onChange={e => { setEmail(e.target.value); setEmailError(''); setGeneralError(''); }}
                    disabled={isLoading}
                    className="flex-1 h-full pr-3 bg-transparent text-sm text-slate-900 focus:outline-none placeholder:text-slate-400 disabled:opacity-60"
                    autoFocus />
                </div>
                {emailError && <p className="text-xs text-red-500 flex items-center gap-1"><svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>{emailError}</p>}
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <div className={`flex h-10 w-full rounded-xl border bg-slate-50 overflow-hidden transition-all ${passwordError ? 'border-red-400 bg-red-50 ring-1 ring-red-300' : 'border-slate-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-200'}`}>
                  <span className="flex items-center pl-3 pr-2">
                    <Lock className={`h-4 w-4 ${passwordError ? 'text-red-400' : 'text-slate-400'}`} />
                  </span>
                  <input type={showPass ? 'text' : 'password'} autoComplete="current-password" placeholder="Enter your password"
                    value={password} onChange={e => { setPassword(e.target.value); setPasswordError(''); setGeneralError(''); }}
                    disabled={isLoading}
                    className="flex-1 h-full bg-transparent text-sm text-slate-900 focus:outline-none placeholder:text-slate-400 disabled:opacity-60" />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    className="px-3 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer" tabIndex={-1}>
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordError && <p className="text-xs text-red-500 flex items-center gap-1"><svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>{passwordError}</p>}
              </div>

              {/* Remember me */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2.5">
                  <button type="button" role="checkbox" aria-checked={rememberMe}
                    onClick={() => setRememberMe(v => !v)} disabled={isLoading}
                    className={`h-5 w-5 rounded-[5px] border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${rememberMe ? 'bg-indigo-500 border-indigo-500' : 'bg-white border-slate-300'} disabled:opacity-60`}>
                    {rememberMe && <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                  </button>
                  <span className="text-sm text-slate-600 select-none">Remember me</span>
                </div>
              </div>

              {/* Sign in */}
              <button type="submit" disabled={isLoading}
                className="w-full h-10 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/30 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none active:scale-[0.98] cursor-pointer">
                {isLoading ? <><Loader2 className="h-4 w-4 animate-spin" />Signing in…</> : <>Sign In <ArrowRight className="h-4 w-4" /></>}
              </button>

              <p className="text-center text-sm text-slate-500">
                Don&apos;t have an account?{' '}
                <Link href="/signup/patient" className="text-indigo-600 font-semibold hover:text-indigo-700 hover:underline transition-colors">
                  Sign up
                </Link>
              </p>
            </form>

            {/* Switch to provider */}
            <div className="px-5 pb-5 pt-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-slate-100" />
                <span className="text-xs text-slate-400">or</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <Link href="/login/provider"
                className="flex items-center justify-between w-full px-4 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center shadow-sm">
                    <Stethoscope className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-700">Healthcare Provider?</p>
                    <p className="text-xs text-emerald-600">Sign in to your provider portal</p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-emerald-500 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>

          <div className="text-center text-xs text-slate-400 mt-4 px-4">
            By continuing, you agree to our{' '}
            <span className="underline cursor-pointer hover:text-slate-600 transition-colors">Terms</span>
            {' '}&amp;{' '}
            <span className="underline cursor-pointer hover:text-slate-600 transition-colors">Privacy Policy</span>
          </div>
        </div>
      </div>
    </div>
  );
}
